// src/renderer/App.tsx
import { useEffect, useRef, useState } from 'react';
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';

import { SimPage } from './pages/SimPage';

import BottomTabBar from './components/BottomTabBar/BottomTabBar';
import CommandsPage from './components/CommandListView/CommandsListPage';
import EventsOverviewPage from './pages/EventsPage';

import AppHeader from './components/AppHeader/AppHeader';
import ToastProvider from './components/ToastProvider/ToastProvider';
import { NetworkGuard } from './components/NetworkGuard/NetworkGuard';

import FleetMapPage from './pages/FleetMapPage';
import DevicesHomePage from './pages/DevicesHomePage';
import { MapDockProvider } from './context/MapDockContext';
import GlobalMapDock from './components/MapDock/GlobalMapDock';
import AdminPage from './pages/AdminPage';
import LoginScreen from './pages/LoginPage';
import LoginLoadingScreen from './pages/LoginLoadingPage';
import StartupOfflineScreen from './pages/StageupOfflineScreen';

import { AuthProvider, useAuth } from './context/AuthContext';

import useStartupOnlineCheck from './hooks/useStartupOnlineCheck';
import LogViewerPage from './pages/LogViewerPage';

const STORAGE_OPERATOR_EVENT = 'fleetmanager:operator:activeEvent';

export default function App() {
  window.logAPI.sendLog('info', '[Renderer] App component rendered');

  return (
    <AuthProvider>
      <Router>
        <MapDockProvider>
          <AppContent />
          <ToastProvider />
        </MapDockProvider>
      </Router>
    </AuthProvider>
  );
}

function AppContent() {
  const { setIdToken, userEmail, userName, role, isAdmin, isOperator } = useAuth();

  const [authenticated, setAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [loginInProgress, setLoginInProgress] = useState(false);
  const [operatorActiveEventId, setOperatorActiveEventId] = useState<string | null>(null);
  const [operatorActiveEventName, setOperatorActiveEventName] = useState<string | null>(null);
  /** Optional race filter when loading operator-scoped event devices (set from Events → Devices). */
  const [operatorScopedRaceId, setOperatorScopedRaceId] = useState<string | null>(null);
  const [operatorScopedUnretrievedOnly, setOperatorScopedUnretrievedOnly] = useState(false);
  // Live (WebSocket) feed health. Default true to avoid a flash before first connect;
  // we only surface the banner once we've connected at least once and then dropped.
  const [liveFeedConnected, setLiveFeedConnected] = useState(true);
  const [liveFeedEverConnected, setLiveFeedEverConnected] = useState(false);
  const initialOnline = useStartupOnlineCheck();
  const navigate = useNavigate();
  const location = useLocation();

  const setIdTokenRef = useRef(setIdToken);
  const navigateRef = useRef(navigate);
  const doLogoutRef = useRef(() => {});
  setIdTokenRef.current = setIdToken;
  navigateRef.current = navigate;

  const isFullscreenRoute = location.pathname.startsWith('/fullscreen');

  // The main process (src/main/services/auth/Auth.ts) is the sole authority on token
  // expiry/refresh — it already retries a failed refresh unless the token is actually
  // dead. This must only react to its AuthFailure event, not run its own expiry timer:
  // a second client-side clock raced the main process's silent-refresh-on-resume after
  // sleep/wake, firing first and logging users out while a valid refresh was still in
  // flight.
  const doLogout = () => {
    try { localStorage.removeItem(STORAGE_OPERATOR_EVENT); } catch {}
    setAuthenticated(false);
    setIdToken(null);
    setLoginInProgress(false);
    setOperatorActiveEventId(null);
    setOperatorActiveEventName(null);
    setOperatorScopedRaceId(null);
    setOperatorScopedUnretrievedOnly(false);
    navigate('/');
  };
  doLogoutRef.current = doLogout;

  // Register auth listeners once; refs ensure callbacks see latest setIdToken/navigate and we don't add duplicate listeners.
  useEffect(() => {
    setAuthChecked(true);

    window.authAPI.onAuthSuccess((data) => {
      setIdTokenRef.current(data?.idToken ?? null);
      setAuthenticated(true);
      setAuthChecked(true);
      setLoginInProgress(false);
      navigateRef.current('/');
    });

    window.authAPI.onAuthFailure(() => {
      doLogoutRef.current();
      setAuthChecked(true);
      setLoginInProgress(false);
    });

    window.authAPI.onAuthRefreshed((data) => {
      setIdTokenRef.current(data?.idToken ?? null);
    });

    window.authAPI.onLoginWindowClosed(() => {
      setLoginInProgress(false);
    });
  }, []);

  // Track live-feed (WebSocket) connection state so we can show a "reconnecting"
  // indicator instead of silently rendering every device as offline when the
  // socket drops.
  useEffect(() => {
    if (!window.fleetAPI?.onWsConnectionState) return;
    const unsubscribe = window.fleetAPI.onWsConnectionState(({ connected }) => {
      setLiveFeedConnected(connected);
      if (connected) setLiveFeedEverConnected(true);
    });
    return () => unsubscribe?.();
  }, []);

  useEffect(() => {
    if (!authenticated || isAdmin || !isOperator) {
      setOperatorActiveEventId(null);
      setOperatorActiveEventName(null);
      setOperatorScopedRaceId(null);
      setOperatorScopedUnretrievedOnly(false);
      return;
    }
    // Restore operator event scope after page refresh (web app).
    setOperatorActiveEventId((prev) => {
      if (prev) return prev;
      try {
        const raw = localStorage.getItem(STORAGE_OPERATOR_EVENT);
        if (!raw) return null;
        const saved = JSON.parse(raw) as { eventId?: unknown; eventName?: unknown; raceId?: unknown; unretrievedOnly?: unknown };
        const eid = typeof saved?.eventId === 'string' ? saved.eventId.trim() : '';
        if (!eid) return null;
        setOperatorActiveEventName(typeof saved.eventName === 'string' ? saved.eventName : null);
        setOperatorScopedRaceId(typeof saved.raceId === 'string' ? saved.raceId : null);
        setOperatorScopedUnretrievedOnly(saved.unretrievedOnly === true);
        return eid;
      } catch { return null; }
    });
  }, [authenticated, isAdmin, isOperator]);

  useEffect(() => {
    if (!isOperator || isAdmin) { try { localStorage.removeItem(STORAGE_OPERATOR_EVENT); } catch {} return; }
    if (!operatorActiveEventId) { try { localStorage.removeItem(STORAGE_OPERATOR_EVENT); } catch {} return; }
    try {
      localStorage.setItem(STORAGE_OPERATOR_EVENT, JSON.stringify({
        eventId: operatorActiveEventId,
        eventName: operatorActiveEventName,
        raceId: operatorScopedRaceId,
        unretrievedOnly: operatorScopedUnretrievedOnly,
      }));
    } catch {}
  }, [isAdmin, isOperator, operatorActiveEventId, operatorActiveEventName, operatorScopedRaceId, operatorScopedUnretrievedOnly]);

  useEffect(() => {
    if (!authenticated || !isOperator || isAdmin) return;
    if (operatorActiveEventId) return;
    if (location.pathname === '/events') return;
    navigate('/events');
  }, [authenticated, isAdmin, isOperator, location.pathname, navigate, operatorActiveEventId]);

  const handleOperatorNavigateToDevices = (payload: {
    eventId: string;
    eventName?: string | null;
    raceId?: string | null;
    unretrievedOnly?: boolean;
  }) => {
    const eid = String(payload.eventId ?? '').trim();
    if (!eid) return;
    setOperatorActiveEventId(eid);
    setOperatorActiveEventName(payload.eventName ?? null);
    const rid = String(payload.raceId ?? '').trim();
    setOperatorScopedRaceId(rid || null);
    setOperatorScopedUnretrievedOnly(!!payload.unretrievedOnly);
    navigate('/');
  };

  useEffect(() => {
    if (!authenticated) return;
    if (!window.windowAPI?.setFullscreen) return;

    window.windowAPI.setFullscreen(isFullscreenRoute);

    return () => {
      window.windowAPI.setFullscreen(false);
    };
  }, [authenticated, isFullscreenRoute]);

  useEffect(() => {
    if (!authenticated) return;
    if (!isFullscreenRoute) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate('/');
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [authenticated, isFullscreenRoute, navigate]);

  const handleLogin = () => {
    if (loginInProgress) return;
    setLoginInProgress(true);
    window.authAPI.loginWithCognito();
  };

  if (!authChecked) return null;

  if (initialOnline === null) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100 dark:bg-slate-800">
        <p className="text-gray-700 dark:text-slate-300 text-lg">Checking network...</p>
      </div>
    );
  }

  if (initialOnline === false) return <StartupOfflineScreen />;

  if (!authenticated) {
    return (
      <>
        <NetworkGuard />
        {loginInProgress ? (
          <LoginLoadingScreen />
        ) : (
          <LoginScreen onLogin={handleLogin} loading={loginInProgress} />
        )}
      </>
    );
  }

  if (isFullscreenRoute) {
    return (
      <>
        <div className="h-screen w-screen font-sans">
          <Routes>
            <Route
              path="/fullscreen/map"
              element={
                <FleetMapPage
                  variant="page"
                  operatorScopedEventId={isOperator && !isAdmin ? operatorActiveEventId : null}
                  operatorScopedEventName={isOperator && !isAdmin ? operatorActiveEventName : null}
                  operatorScopedRaceId={isOperator && !isAdmin ? operatorScopedRaceId : null}
                />
              }
            />
            <Route
              path="/fullscreen/logs"
              element={
                <LogViewerPage
                  operatorScopedEventId={isOperator && !isAdmin ? operatorActiveEventId : null}
                  operatorScopedEventName={isOperator && !isAdmin ? operatorActiveEventName : null}
                />
              }
            />
            <Route path="/fullscreen/sim-page" element={<SimPage />} />
            <Route
              path="/fullscreen/events"
              element={
                <EventsOverviewPage
                  operatorActiveEventId={operatorActiveEventId}
                  onOperatorEventContinue={(event) => {
                    setOperatorActiveEventId(event.eventId);
                    setOperatorActiveEventName(event.name ?? null);
                    setOperatorScopedRaceId(null);
                    setOperatorScopedUnretrievedOnly(false);
                  }}
                  onOperatorEventClear={() => {
                    setOperatorActiveEventId(null);
                    setOperatorActiveEventName(null);
                    setOperatorScopedRaceId(null);
                    setOperatorScopedUnretrievedOnly(false);
                  }}
                  onOperatorNavigateToDevices={
                    isOperator && !isAdmin ? handleOperatorNavigateToDevices : undefined
                  }
                />
              }
            />
            <Route path="/fullscreen/admin" element={<AdminPage />} />
            <Route
              path="/fullscreen/commands/:requestId"
              element={
                <CommandsPage
                  operatorScopedEventId={isOperator && !isAdmin ? operatorActiveEventId : null}
                />
              }
            />
            <Route path="*" element={<Navigate to="/fullscreen/map" replace />} />
          </Routes>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="h-screen font-sans">
        <div className="fixed top-0 left-0 right-0 z-0">
        <NetworkGuard />
        <AppHeader userName={userName} userEmail={userEmail} role={role} onLogout={doLogout} />
        </div>

        {liveFeedEverConnected && !liveFeedConnected ? (
          <div className="fixed top-[56px] left-0 right-0 z-20 flex justify-center pointer-events-none">
            <div className="mt-2 flex items-center gap-2 rounded-full bg-amber-500/95 px-3 py-1 text-xs font-medium text-white shadow-md">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-white" />
              Reconnecting live updates… device status may be stale
            </div>
          </div>
        ) : null}

        <div className="absolute top-[56px] left-0 right-0 overflow-y-auto" style={{ background: 'var(--bg-base)', bottom: 'calc(60px + env(safe-area-inset-bottom))' }}>
          <Routes>
          <Route path="/sim-page" element={<SimPage />} />
          <Route
            path="/commands/:requestId"
            element={
              <CommandsPage
                operatorScopedEventId={isOperator && !isAdmin ? operatorActiveEventId : null}
              />
            }
          />
          <Route path="/admin" element={<AdminPage />} />
          <Route
            path="/events"
            element={
              <EventsOverviewPage
                operatorActiveEventId={operatorActiveEventId}
                onOperatorEventContinue={(event) => {
                  setOperatorActiveEventId(event.eventId);
                  setOperatorActiveEventName(event.name ?? null);
                  setOperatorScopedRaceId(null);
                  setOperatorScopedUnretrievedOnly(false);
                }}
                onOperatorEventClear={() => {
                  setOperatorActiveEventId(null);
                  setOperatorActiveEventName(null);
                  setOperatorScopedRaceId(null);
                  setOperatorScopedUnretrievedOnly(false);
                }}
                onOperatorNavigateToDevices={
                  isOperator && !isAdmin ? handleOperatorNavigateToDevices : undefined
                }
              />
            }
          />
          <Route
            path="/logs"
            element={
              <LogViewerPage
                defaultViewMode="logs"
                operatorScopedEventId={isOperator && !isAdmin ? operatorActiveEventId : null}
                operatorScopedEventName={isOperator && !isAdmin ? operatorActiveEventName : null}
              />
            }
          />
          <Route
            path="/file-explorer"
            element={
              <LogViewerPage
                defaultViewMode="data"
                operatorScopedEventId={isOperator && !isAdmin ? operatorActiveEventId : null}
                operatorScopedEventName={isOperator && !isAdmin ? operatorActiveEventName : null}
              />
            }
          />
          <Route
            path="*"
            element={
              <DevicesHomePage
                operatorScopedEventId={isOperator && !isAdmin ? operatorActiveEventId : null}
                operatorScopedEventName={isOperator && !isAdmin ? operatorActiveEventName : null}
                operatorScopedRaceId={isOperator && !isAdmin ? operatorScopedRaceId : null}
                operatorScopedUnretrievedOnly={isOperator && !isAdmin ? operatorScopedUnretrievedOnly : false}
              />
            }
          />
          </Routes>
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-10">
          <BottomTabBar />
        </div>
      </div>
      <GlobalMapDock
        operatorScopedEventId={isOperator && !isAdmin ? operatorActiveEventId : null}
        operatorScopedEventName={isOperator && !isAdmin ? operatorActiveEventName : null}
        operatorScopedRaceId={isOperator && !isAdmin ? operatorScopedRaceId : null}
      />
    </>
  );
}